import { useEffect, useState } from 'react';
import { ResourcePermission, ResourcePermissionRole, TenantShare } from '../../chat/types';
import {
  getDocumentShares,
  addDocumentShares,
  deleteDocumentShare,
  updateDocumentShare,
  deleteDocumentGroupShare,
  shareWithOrganisation,
  unshareFromOrganisation
} from '../../document-hub/api';

interface GroupShareInfo {
  group_id: string;
  group_name: string;
  permission: ResourcePermissionRole;
}

type UseDocumentShareProps = {
  externalId?: string;
  isOwner: boolean;
  /** Called after an org share is added or removed so the caller can refresh document metadata. */
  onOrgShareChange?: () => void;
};

const useDocumentShare = ({ externalId, isOwner, onOrgShareChange }: UseDocumentShareProps) => {
  const [existingShares, setExistingShares] = useState<ResourcePermission[]>([]);
  const [groupShares, setGroupShares] = useState<GroupShareInfo[]>([]);
  const [tenantShare, setTenantShare] = useState<TenantShare | null>(null);
  const [sharesLoading, setSharesLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    const clearShares = () => {
      setExistingShares([]);
      setGroupShares([]);
      setTenantShare(null);
    };

    if (externalId && isOwner) {
      clearShares();
      setSharesLoading(true);
      getDocumentShares(externalId)
        .then((data) => {
          if (cancelled) return;
          setExistingShares(data.user_shares);
          setGroupShares(data.group_shares ?? []);
          setTenantShare(data.tenant_share ?? null);
        })
        .catch((error) => {
          if (cancelled) return;
          console.error('Error fetching document shares:', error);
          clearShares();
        })
        .finally(() => {
          if (!cancelled) setSharesLoading(false);
        });
    } else {
      clearShares();
      setSharesLoading(false);
    }

    return () => {
      cancelled = true;
    };
  }, [externalId, isOwner]);

  const handleAddPeople = async (
    emailList: string[],
    permission: ResourcePermissionRole,
    message: string,
    groupIds?: string[]
  ) => {
    const response = await addDocumentShares(externalId!, emailList, permission, message, groupIds);
    setExistingShares([...existingShares, ...response]);

    if (groupIds?.length) {
      const refreshed = await getDocumentShares(externalId!);
      setGroupShares(refreshed.group_shares ?? []);
    }
  };

  const handleRemovePerson = async (permissionId: string) => {
    await deleteDocumentShare(externalId!, permissionId);
    setExistingShares(existingShares.filter((share) => share.permission_id !== permissionId));
  };

  const handleUpdatePermission = async (permissionId: string, permission: ResourcePermissionRole) => {
    await updateDocumentShare(externalId!, permissionId, permission);
    setExistingShares(
      existingShares.map((share) => (share.permission_id === permissionId ? { ...share, role: permission } : share))
    );
  };

  const handleRemoveGroupShare = async (groupId: string) => {
    if (!externalId) return;
    await deleteDocumentGroupShare(externalId, groupId);
    setGroupShares(groupShares.filter((g) => g.group_id !== groupId));
  };

  const handleShareWithOrganisation = async (_permission: ResourcePermissionRole) => {
    if (!externalId) return;
    await shareWithOrganisation(externalId);
    const refreshed = await getDocumentShares(externalId);
    setTenantShare(refreshed.tenant_share ?? null);
    onOrgShareChange?.();
  };

  const handleUnshareFromOrganisation = async () => {
    if (!externalId) return;
    await unshareFromOrganisation(externalId);
    setTenantShare(null);
    onOrgShareChange?.();
  };

  return {
    sharesLoading,
    existingShares,
    groupShares,
    tenantShare,
    handleAddPeople,
    handleRemovePerson,
    handleUpdatePermission,
    handleRemoveGroupShare,
    handleShareWithOrganisation,
    handleUnshareFromOrganisation
  };
};

export default useDocumentShare;
